<script lang="ts" setup>
import { computed, ref, onMounted, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useDebounceFn } from '@vueuse/core';
import type { FieldSchema } from '@FormModule';
import { useSessionStore } from '@Stores';

const props = defineProps<{
  creditCardNameRequired?: boolean;
  creditCardNumberRequired?: boolean;
  error?: string | boolean;
  field?: FieldSchema;
}>();

const { shelterProfile } = storeToRefs(useSessionStore());

let cardElement: any;
let elements: any;
let stripe: any;
let stripePromise: Promise<any> | null = null;

const cardError = ref('');
const cardHolderName = ref('');
const creditCardToken = ref<any>('');
const showProgressBar = ref(false);
const model = defineModel({
  default: {
    cardHolderName: '',
    creditCardToken: '',
  },
});

const creditCardNameRequired = computed(
  () => props?.field?.settings?.creditCardNameRequired ?? props.creditCardNameRequired ?? true
);
const creditCardNumberRequired = computed(
  () => props?.field?.settings?.creditCardNumberRequired ?? props.creditCardNameRequired ?? true
);
const showCardError = computed(() => cardError.value || (!!props.error && !creditCardToken.value));
const showCreditCardFee = computed(
  () => shelterProfile.value?.charge_adopter_cc_fee && Number(props.field?.settings?.cc_fee ?? 0) > 0
);
const _model = computed(() => {
  return {
    cardHolderName: cardHolderName.value,
    creditCardToken: creditCardToken.value,
    stripeError: cardError.value,
  };
});

onMounted(() => setupStripe());

watch(_model, () => (model.value = _model.value));
watch(cardError, onCardErrorUpdate);

// HELPERS
async function setupStripe() {
  if (!stripePromise) {
    const { loadStripe } = await import('@stripe/stripe-js');
    stripePromise = loadStripe(import.meta.env.VITE_STRIPE_KEY);
  }

  stripe = await stripePromise;
  elements = stripe.elements();
  cardElement = elements.create('card', { style: { base: { fontSize: '14px' } } });

  const debouncedValidateCard = useDebounceFn(() => {
    onValidateCard();
  }, 500);

  cardElement.on('change', () => debouncedValidateCard());

  cardElement.mount('#card-element');
}

function onCardErrorUpdate() {
  if (!props.field) return;

  const STRIPE_ERROR_KEY = 'components.forms.errors.stripeValidationError';

  if (cardError.value) {
    props.field.state.errors.add(STRIPE_ERROR_KEY);
    return;
  }

  props.field.state.errors.delete(STRIPE_ERROR_KEY);
}

async function onValidateCard() {
  showProgressBar.value = true;
  cardError.value = '';
  creditCardToken.value = ''; // Clear token when re-validating

  const { token, error: stripeError } = await stripe.createToken(cardElement, {
    name: cardHolderName.value,
  });

  if (stripeError) {
    cardError.value = stripeError.message;
    creditCardToken.value = ''; // Ensure token is cleared on error
    showProgressBar.value = false;
    return;
  }

  creditCardToken.value = token;
  cardError.value = ''; // Clear error on success
  showProgressBar.value = false;
}
</script>

<template>
  <div class="flex flex-col gap-6">
    <div class="relative flex w-full flex-col">
      <AFieldLabel
        :required="creditCardNameRequired"
        :error="error && !cardHolderName"
        :label="$t('common.name')"
      />

      <AFieldInputText
        v-model="cardHolderName"
        :required="creditCardNameRequired"
        :error="error && !cardHolderName"
        :placeholder="$t('cart.paymentMethod.validateName')"
      />
    </div>

    <div class="relative flex w-full flex-col">
      <AFieldLabel
        :required="creditCardNumberRequired"
        :error="showCardError"
        :label="$t('cart.label.creditCard')"
      />

      <div
        id="card-element"
        :class="[
          'rounded-md border border-slate-300 px-3 py-2',
          showCardError ? '!placeholder:text-red-600 !border-red-600' : 'border-slate-300',
        ]"
      ></div>

      <p
        v-if="showCreditCardFee && field"
        class="text-sm text-gray-500"
      >
        {{ $t('cart.splitPayment.creditCardFee') }} ${{ field.settings?.cc_fee ?? 0 }}
      </p>

      <AFieldError
        v-if="!showProgressBar"
        :error="cardError"
      />

      <div
        v-if="showProgressBar"
        class="flex h-5 items-center gap-2"
      >
        <AFieldLabel
          class="text-xs text-slate-500"
          :label="$t('cart.paymentMethod.validating')"
        />

        <ProgressBar
          class="h-0.5 w-full"
          mode="indeterminate"
          pt:value="bg-brand-400"
        />
      </div>
    </div>
  </div>
</template>
